/* eslint-disable jsx-a11y/accessible-emoji */
import { DrugItem } from 'src/api/types';
import { ParsedUrlQuery } from 'querystring';
import { IntlShape } from 'react-intl';
import capitalize from 'lodash/capitalize';
import {
  NEXT_PUBLIC_ENV,
  NEXT_PUBLIC_ENV_VARIANT,
  getEnvVariable
} from 'src/utils/envVariablesHelper';
import { PROD_DOMAIN } from '../api/constants';
import { PostDrugResponse } from '../api/requests/postDrugSearch';
import trns from '../page-components/drug/trns';
import { Locales } from './localization';

interface MetaData {
  title: string;
  description: string;
}

interface GetProductMetaDataProps {
  drugName: string;
  locale: string;
  minPrice?: number;
  cityName?: string;
}

export const getGeneralPagesMetaData = (path: string, locale: string) => {
  const isRu = locale === Locales.RU;
  let title = '';
  let description = isRu
    ? 'Справочник лекарственных средств 💊, содержит подробные описания и инструкцию по применению лекарственных препаратов, информацию про противопоказания и побочные действия.'
    : 'Довідник лікарських засобів 💊, містить докладні описи та інструкцію щодо застосування лікарських засобів, інформацію про протипоказання та побічні дії.';
  if (path === '/') {
    title = isRu
      ? 'likie.ua - поиск и бронирование лекарств в аптеках, инструкции | LIKIE | ЛІКИЄ | ЛИКИЕ'
      : 'likie.ua - пошук та бронювання ліків в аптеках, інструкції | LIKIE | ЛІКИЄ | ЛИКИЕ';
    description = isRu
      ? 'Поиск и бронирование лекарственных средств в аптеках 💊 Низкие цены 💊 Подробное описание с инструкциями по применению 💊 LIKIE | ЛІКИЄ | ЛИКИЕ'
      : 'Пошук та бронювання лікарських засобів в аптеках 💊 Низькі ціни 💊 Докладний опис з інструкціями щодо застосування 💊 LIKIE | ЛІКИЄ | ЛИКИЕ';
  } else if (path === '/help') {
    title = isRu
      ? 'Ответы на часто задаваемые вопросы | LIKIE'
      : 'Відповіді на поширені питання | LIKIE';
  } else if (path === '/restoringconnection/[clientHash]') {
    title = isRu ? 'Мои заказы | LIKIE' : 'Мої замовлення | LIKIE';
  } else {
    title = isRu
      ? 'Соглашение о конфиденциальности и защите персональных данных | LIKIE'
      : 'Угода про конфіденційність та захист персональних даних | LIKIE';
  }
  return {
    title,
    description
  };
};

export const getCategoryPageMetaData = (
  categoryName: string,
  pageNumber: number,
  locale?: string
): MetaData => {
  const isRu = locale === Locales.RU;
  const metaTitle = isRu
    ? `${categoryName} – цены, отзывы, аналоги, инструкции, купить в аптеках Украины`
    : `${categoryName} – ціни, відгуки, аналоги, інструкції, купити в аптеках України`;
  const metaDescription = isRu
    ? `Купить ${categoryName} – выгодная цена ✅ большой выбор аптек ✅ отзывы и инструкции по применению ✅`
    : `Купити ${categoryName} – вигідна ціна ✅ великий вибір аптек ✅ відгуки та інструкції щодо застосування ✅`;
  const paginatedMetaTitle = `${metaTitle} – страница ${pageNumber}`;
  const paginatedMetaDescription = `${metaDescription} – страница ${pageNumber}`;

  if (pageNumber && pageNumber > 1) {
    return {
      title: paginatedMetaTitle,
      description: paginatedMetaDescription
    };
  }

  return { title: metaTitle, description: metaDescription };
};

export const getSearchPageMetaData = (
  search: string | string[],
  locale?: string
): MetaData => {
  const isRu = locale === Locales.RU;
  const metaTitle = isRu
    ? `${search} - поиск и бронирование лекарств в аптеках, инструкции | LIKIE | ЛІКИЄ | ЛИКИЕ`
    : `${search} - пошук та бронювання ліків в аптеках, інструкції | LIKIE | ЛІКИЄ | ЛИКИЕ`;
  const metaDescription = isRu
    ? 'Поиск и бронирование лекарственных средств в аптеках 💊 Низкие цены 💊 Подробное описание с инструкциями по применению 💊 LIKIE | ЛІКИЄ | ЛИКИЕ'
    : 'Пошук та бронювання лікарських засобів в аптеках 💊 Низькі ціни 💊 Докладний опис з інструкціями щодо застосування 💊 LIKIE | ЛІКИЄ | ЛИКИЕ';
  return { title: metaTitle, description: metaDescription };
};

export const getProductPageMetaData = (
  drugData: DrugItem,
  locale?: string
): MetaData => {
  const isRu = locale === Locales.RU;
  const metaTitle = isRu
    ? `💊 ${drugData.name} купить - инструкция, цена, отзывы, состав, аналоги в Украине: Днепр, Киев, Харьков, Одесса ${drugData.foundInBranchesCount} | Likie`
    : `💊 ${drugData.name} купити - інструкція, ціна, відгуки, склад, аналоги в Україні: Дніпро, Київ, Харків, Одеса ${drugData.foundInBranchesCount} | Likie`;
  const metaDescription = isRu
    ? `Купить ${drugData.name} цена от ${
        Math.round(drugData.minPrice * 100) / 100
      } грн - заказать на Likie.ua. Поиск и бронирование лекарств в аптеках по всей Украине! 💊 Низкие цены 💊 ${
        drugData.foundInBranchesCount
      }`
    : `Купити ${drugData.name} ціна від ${
        Math.round(drugData.minPrice * 100) / 100
      } грн - замовити на Likie.ua. Пошук та бронювання лікарських засобів в аптеках по всій Україні! 💊 Найнижчі ціни 💊 ${
        drugData.foundInBranchesCount
      }`;

  return { title: metaTitle, description: metaDescription };
};

export const getProductAnaloguePageMetaData = (
  drugData: DrugItem,
  locale?: string
): MetaData => {
  const isRu = locale === Locales.RU;
  const metaTitle = isRu
    ? `Аналоги препарата ${drugData.name} - в Украине: Днепр, Киев, Харьков, Одесса, ${drugData.foundInBranchesCount} | Likie`
    : `Аналоги препарату ${drugData.name}- в Україні: Дніпро, Києв, Харків, Одеса, ${drugData.foundInBranchesCount} | Likie`;
  const metaDescription = isRu
    ? `Предлагаем аналоги препарата ${drugData.name}! ✅ Быстрое бронирование ✈ в ближайшей аптеке ➕ вашего города по самой низкой цене!`
    : `Пропунуємо аналоги препарату ${drugData.name}! ✅ Швидке бронювання ✈ в найближчій аптеці ➕ вашого міста по найнижчій ціні!`;

  return { title: metaTitle, description: metaDescription };
};

export const getCartPageMetaData = (locale?: string): MetaData => {
  const isRu = locale === Locales.RU;
  const metaTitle = isRu
    ? 'likie.ua - поиск и бронирование лекарств в аптеках, инструкции | LIKIE | ЛІКИЄ | ЛИКИЕ'
    : 'likie.ua - пошук та бронювання ліків в аптеках, інструкції | LIKIE | ЛІКИЄ | ЛИКИЕ';
  const metaDescription = isRu
    ? 'Поиск и бронирование лекарственных средств в аптеках 💊 Низкие цены 💊 Подробное описание с инструкциями по применению 💊 LIKIE | ЛІКИЄ | ЛИКИЕ'
    : 'Пошук та бронювання лікарських засобів в аптеках 💊 Низькі ціни 💊 Докладний опис з інструкціями щодо застосування 💊 LIKIE | ЛІКИЄ | ЛИКИЕ';
  return { title: metaTitle, description: metaDescription };
};

export const getSchemaData = (
  finalEntityName: string,
  popularDrugs: PostDrugResponse,
  cheapDrugs: PostDrugResponse,
  intl: IntlShape
) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: `${intl.formatMessage(
        trns.good
      )} ${finalEntityName} ${intl.formatMessage(trns.homeDelivery)}`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `${intl.formatMessage(trns.oftenBuy)}: ${popularDrugs?.drugs
          .slice(0, 3)
          .map((drug) => {
            return `<a href=${`${PROD_DOMAIN}/product/${drug?.translitName}`}>
                      ${drug?.name}
                  </a>,`;
          })}`
      }
    },
    {
      '@type': 'Question',
      name: `${intl.formatMessage(
        trns.isCheap
      )} ${finalEntityName} ${intl.formatMessage(trns.isInPharmacy)}`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `${intl.formatMessage(
          trns.isCheapGoods
        )} ${finalEntityName}, ${intl.formatMessage(trns.topCheapGoods, {
          topGoods: intl.formatMessage(trns.topGoods)
        })}: ${cheapDrugs?.drugs.slice(0, 3).map((drug) => {
          return `<a href=${`${PROD_DOMAIN}/product/${drug?.translitName}`}>${
            drug?.name
          }</a> ${intl.formatMessage(trns.priceFrom)} ${drug?.minPrice || 0},`;
        })}`
      }
    },
    {
      '@type': 'Question',
      name: `${intl.formatMessage(
        trns.canBuy
      )} ${finalEntityName} ${intl.formatMessage(trns.withDiscount)}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `${intl.formatMessage(trns.topGoods)}: ${cheapDrugs?.drugs
          .slice(0, 3)
          .map((drug) => {
            return `<a href=${`${PROD_DOMAIN}/product/${drug?.translitName}`}>${
              drug?.name
            }</a>,`;
          })}`
      }
    },
    {
      '@type': 'Question',
      name: `${intl.formatMessage(
        trns.canOrder
      )} ${finalEntityName} ${intl.formatMessage(trns.canOrderOnline)}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `${intl.formatMessage(
          trns.yesCanBuy
        )} ${finalEntityName} ${intl.formatMessage(
          trns.paymentMethods
        )} <a href=${`${PROD_DOMAIN}/privacy_policy`}> ${intl.formatMessage(
          trns.paymentPart
        )}</a>.`
      }
    }
  ]
});

const preparedToLowerCase = (str: string) => str.toLowerCase();
const preparedToUpperFirstLetter = (str: string) =>
  str[0] + str.slice(1).toLowerCase();

const getMetaTitle = (isRu: boolean, minPrice?: number): string => {
  if (minPrice === undefined || minPrice <= 0) {
    return '';
  }

  const price = minPrice?.toFixed(2);

  return isRu ? ` от ${price} грн,` : ` від ${price} грн,`;
};

export const getProductByLetterPageAndCityMetaData = ({
  drugName,
  locale,
  minPrice,
  cityName
}: GetProductMetaDataProps): MetaData => {
  const isRu = locale === Locales.RU;
  const minPriceString = getMetaTitle(isRu, minPrice);

  const metaTitle = isRu
    ? `Купить ${preparedToLowerCase(
        drugName
      )} ${cityName} –${minPriceString} наличие и цена в аптеках - Likie`
    : `Купити ${preparedToLowerCase(
        drugName
      )} ${cityName} –${minPriceString} наявність та ціна в аптеках - Likie`;
  const metaDescription = isRu
    ? `${capitalize(
        drugName
      )} ➤ ${cityName} 💊 Низкие цены ☝ Быстрая бронь / Самовывоз ✈️ Онлайн заказ ⭐ Самая дешевая цена в аптеках`
    : `${capitalize(
        drugName
      )} ➤ ${cityName} 💊 Низькі ціни ☝ Швидка бронь / Самовивіз ✈️ Онлайн замовлення ⭐ Найдешевша ціна в аптеках`;
  return { title: metaTitle, description: metaDescription };
};

export const getProductByLetterPageMetaData = ({
  drugName,
  locale,
  minPrice
}: GetProductMetaDataProps): MetaData => {
  const isRu = locale === Locales.RU;
  const minPriceString = getMetaTitle(isRu, minPrice);

  const metaTitle = isRu
    ? `${preparedToUpperFirstLetter(
        drugName
      )} – купить${minPriceString} наличие и цена в аптеках Украины - Likie`
    : `${preparedToUpperFirstLetter(
        drugName
      )} – купити${minPriceString} наявність та ціна в аптеках України - Likie`;
  const metaDescription = isRu
    ? `${capitalize(
        drugName
      )}  ➤ Купить в лицензированных аптеках ☝ Быстрая бронь / Самовывоз ✈️ Онлайн заказ ⭐ Самая дешевая цена в аптеках Украины`
    : `${capitalize(
        drugName
      )}  ➤ Купуй в ліцензованих аптеках ☝ Швидка бронь / Самовивіз ✈️ Онлайн замовлення ⭐ Найдешевша ціна в аптеках України`;
  return { title: metaTitle, description: metaDescription };
};

export const getProductsListByLetterPageMetaData = (
  letter: string,
  locale: string
): MetaData => {
  const isRu = locale === Locales.RU;
  const metaTitle = isRu
    ? `Поиск таблеток и лекарств по названию на букву ${letter} | LIKIE`
    : `Пошук таблеток та ліків за назвою на букву ${letter} | LIKIE`;
  const metaDescription = isRu
    ? `Найти таблетки и лекарства на букву ${letter}, узнать цену в своем городе на продукты известных производителей | LIKIE`
    : `Знайти таблетки та ліки на букву ${letter}, дізнатися ціну у своєму місті на продукти відомих виробників | LIKIE`;
  return { title: metaTitle, description: metaDescription };
};

export const getCatalogPageMetaData = (locale?: string): MetaData => {
  return {
    title: 'Каталог | LikiE',
    description:
      locale === Locales.RU
        ? 'Каталог лекарств | LikiE'
        : 'Каталог ліків | LikiE'
  };
};

export const getBlogPageMetaData = (locale: string): MetaData => {
  const isRu = locale === Locales.RU;
  const metaTitle = isRu
    ? `Блог сервиса поиска лекарств likie`
    : `Блог сервісу пошуку ліків likie`;
  return { title: metaTitle, description: '' };
};

export const getAboutUsMetaData = (locale: string): MetaData => {
  const isRu = locale === Locales.RU;
  const metaTitle = isRu ? `О нас` : `Про нас`;
  const metaDescription = isRu
    ? 'LikiЄ - поможет вам сэкономить вам время и деньги'
    : 'LikiЄ - допоможе вам зекономити ваш час та гроші';
  return { title: metaTitle, description: metaDescription };
};

export const getPromoPageMetaData = (): MetaData => {
  const metaTitle = `Акція «Знижка 50 грн.»`;
  return { title: metaTitle, description: '' };
};

export const getArticlePageMetaData = (
  translitName: string | string[],
  locale?: string
): MetaData => {
  const isRu = locale === Locales.RU;
  switch (translitName) {
    case 'moya-aptechka':
      return {
        title: isRu ? 'Домашняя аптечка' : 'Домашня аптечка',
        description: ''
      };
    case 'shkira-yak-indykator-zakhvoryuvan':
      return {
        title: isRu
          ? 'Кожа как индикатор заболеваний'
          : 'Шкіра як індикатор захворювань',
        description: ''
      };
    case 'uvaga-covid':
      return {
        title: isRu ? 'Внимание, COVID' : 'Увага, COVID',
        description: ''
      };
    case 'dopomogo':
      return {
        title: isRu ? 'Первая медицинская помощь' : 'Перша медична допомога',
        description: ''
      };
    default:
      return {
        title: '',
        description: ''
      };
  }
};

export const analyticsParams = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'fbclid',
  'aid',
  'cid',
  'gid',
  'tid',
  'utm_term',
  'fbclid'
];

export const categoryAllowedParams = [
  'category',
  'params',
  'filters',
  'sortBy',
  'sortDirection',
  ...analyticsParams
];

export const drugAllowedParams = ['translitName', ...analyticsParams];

export const isAllQueryParamsAllowed = (
  query: ParsedUrlQuery,
  allowedParams: string[]
): boolean => {
  let result = true;
  const keys = Object.keys(query);

  keys.forEach((queryElement) => {
    if (!allowedParams?.includes(queryElement)) {
      result = false;
    } else {
      const foundElemIndex = keys.indexOf(queryElement);
      if (foundElemIndex > -1) {
        keys.splice(foundElemIndex, 1);
      }
    }
  });
  return result;
};

export const isNoIndexing =
  getEnvVariable(NEXT_PUBLIC_ENV) === NEXT_PUBLIC_ENV_VARIANT.STAGE ||
  getEnvVariable(NEXT_PUBLIC_ENV) === NEXT_PUBLIC_ENV_VARIANT.DEV;

export const robotsTagContent = isNoIndexing ? 'noindex' : 'index, follow';
